import {
  Box,
  Button,
  Col,
  Divider,
  Grid,
  Group,
  LoadingOverlay,
  Modal,
  NumberInput,
  Select,
  Table,
  Text,
  ActionIcon,
  Badge,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import SelectProductModal from "./SelectProductModal";
import { openConfirmModal } from "@mantine/modals";
import PropTypes from "prop-types";
import React, { forwardRef, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { isCounterAdmin, isManager } from "pages/Auth/permissionGuard";

import { stock_apis } from "./stock-transfer.service";
import { showErrorToast, showSuccessToast } from "../../../utilities/Toast";

const NEGATIVE_DIFFERENCE_OPTIONS = [
  { value: "LOSS", label: "LOSS" },
  { value: "NOT_SENT", label: "NOT_SENT" },
  { value: "WRONG_ENTRY", label: "WRONG_ENTRY" },
];

const POSITIVE_DIFFERENCE_OPTIONS = [
  { value: "EXTRA_SENT", label: "EXTRA_SENT" },
];

const NEGATIVE_DIFFERENCE_TYPES = NEGATIVE_DIFFERENCE_OPTIONS.map(option => option.value);
const POSITIVE_DIFFERENCE_TYPES = POSITIVE_DIFFERENCE_OPTIONS.map(option => option.value);
const DIFFERENCE_EPSILON = 0.0005;

const normalizeQty = value => {
  if (value === null || value === undefined || value === "") {
    return 0;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const formatQty = (value, units) => {
  if (value === null || value === undefined) {
    return "-";
  }

  const normalized = normalizeQty(value);
  if (units && ["PCS", "NOS"].includes(units.toUpperCase())) {
    return normalized.toFixed(0);
  }
  return normalized.toFixed(3);
};

const formatDateTime = value => {
  if (!value) {
    return "-";
  }

  return new Date(value).toLocaleString("en-IN");
};

const getRowStyle = (item, orderStatus) => {
  if (orderStatus === "loss_report") {
    return { backgroundColor: "rgba(103, 65, 217, 0.15)" }; // Loss Report - Purple/Grape
  }
  if (orderStatus === "created" && item.difference_qty === undefined) return {};
  const diff = normalizeQty(item.difference_qty);
  if (Math.abs(diff) < DIFFERENCE_EPSILON) {
    return { backgroundColor: "rgba(40, 167, 69, 0.15)" }; // Matched - Green
  }
  if (diff < -DIFFERENCE_EPSILON) {
    return { backgroundColor: "rgba(220, 53, 69, 0.15)" }; // Shortage - Red
  }
  if (diff > DIFFERENCE_EPSILON) {
    return { backgroundColor: "rgba(255, 193, 7, 0.25)" }; // Excess - Yellow
  }
  return {};
};

const getDifferenceTypeLabel = item => {
  const differenceQty = normalizeQty(item.difference_qty);

  if (Math.abs(differenceQty) < DIFFERENCE_EPSILON) {
    return "MATCHED";
  }

  if (!item.resolved_at) {
    if (differenceQty < 0 && !item.difference_type) {
      return "PENDING_ADMIN_REVIEW";
    }

    if (item.difference_type) {
      return `${item.difference_type} (PENDING_ADMIN_REVIEW)`;
    }
  }

  if (item.orderStatus === "loss_report") {
    return "LOSS";
  }

  return item.difference_type || "-";
};

const recalculateReceivedItem = item => {
  const sentQty = normalizeQty(item.stock);
  const receivedQty = normalizeQty(item.received_qty);
  const rawDifference = receivedQty - sentQty;
  const differenceQty = Math.abs(rawDifference) < DIFFERENCE_EPSILON ? 0 : Number(rawDifference.toFixed(3));
  let differenceType = item.difference_type || null;

  if (differenceQty === 0) {
    differenceType = null;
  }

  if (differenceQty < 0 && differenceType && !NEGATIVE_DIFFERENCE_TYPES.includes(differenceType)) {
    differenceType = null;
  }

  if (differenceQty > 0 && differenceType && !POSITIVE_DIFFERENCE_TYPES.includes(differenceType)) {
    differenceType = null;
  }

  return {
    ...item,
    received_qty: receivedQty,
    difference_qty: differenceQty,
    difference_type: differenceType,
  };
};

const buildReceivedItem = item => {
  const receivedQty = item.received_qty ?? item.stock ?? 0;

  return recalculateReceivedItem({
    id: item.id,
    product: item.product,
    product_id: item.product_id, // Ensure internal IDs are kept
    batch_no: item.batch_no,
    batch_id: item.batch, // Map 'batch' from serializer to 'batch_id'
    mrp: item.mrp,
    selling_price: item.selling_price,
    stock: normalizeQty(item.original_qty ?? item.stock),
    received_qty: normalizeQty(receivedQty),
    difference_type: item.difference_type || null,
    received_by: item.received_by || null,
    received_at: item.received_at || null,
    units: item.units || null,
  });
};

const BatchSelectItem = forwardRef(({ label, batch_no, stockStr, expInfo, isExpired, ...others }, ref) => {
  const displayBatch = batch_no || label;
  return (
    <div ref={ref} {...others} style={{ padding: '8px 12px' }}>
      <Group noWrap position="apart">
        <div style={{ flex: 1, minWidth: 0, marginRight: 10 }}>
          <Text size="sm" weight={600} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {displayBatch}
          </Text>
          {expInfo && (
            <Text
              size="xs"
              italic={isExpired}
              style={{
                marginTop: 2,
                fontWeight: isExpired ? 700 : 500,
                color: isExpired ? '#ff6b6b' : 'inherit',
                opacity: 0.8,
                whiteSpace: 'nowrap'
              }}
            >
              {expInfo}
            </Text>
          )}
        </div>
        {stockStr && (
          <Badge color={isExpired ? "red" : "blue"} variant="light" size="sm" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
            {stockStr}
          </Badge>
        )}
      </Group>
    </div>
  );
});

function StockTransferDetailsModal({ opened, onClose, stockTransferId, showActions, onSuccess }) {
  const [details, setDetails] = useState({});
  const [receivedModalOpened, setReceivedModalOpened] = useState(false);
  const [receivedItems, setReceivedItems] = useState([]);
  const [selectProductModalOpened, setSelectProductModalOpened] = useState(false);
  const [productBatches, setProductBatches] = useState({}); // Cache for product batches {productId: [batches]}
  const queryClient = useQueryClient();
  const counterAdminUser = isCounterAdmin();
  const managerUser = isManager();

  useEffect(() => {
    if (!opened) {
      setReceivedModalOpened(false);
    }
  }, [opened]);

  const detailsQuery = useQuery(
    ["order_data", stockTransferId],
    () => stock_apis.get_stock_transfer_details(stockTransferId),
    {
      enabled: stockTransferId !== null,
      onSuccess: response => {
        const transferDetails = response.data || {};
        setDetails(transferDetails);
        const mappedItems = (transferDetails.stock_list || []).map(item => {
          const baseItem = buildReceivedItem(item);
          if (transferDetails.status === "loss_report") {
            return {
              ...baseItem,
              received_qty: 0,
              difference_qty: -baseItem.stock,
              difference_type: "LOSS",
              orderStatus: "loss_report", // Pass status for labels/styles
            };
          }
          return { ...baseItem, orderStatus: transferDetails.status };
        });
        setReceivedItems(mappedItems);
      },
    }
  );

  const processTransferMutation = useMutation({
    mutationFn: payload => stock_apis.change_transfer_status(payload),
    onSuccess: response => {
      const result = response.data || response;

      if (result?.success) {
        showSuccessToast({
          title: "Success",
          message: result.message || "Stock transfer processed successfully.",
        });
        queryClient.refetchQueries(["order_data", stockTransferId]);
        queryClient.refetchQueries(["stock_list_received"]);
        queryClient.refetchQueries(["stock_list_sent"]);
        if (onSuccess) onSuccess();
        setReceivedModalOpened(false);
        onClose();
      } else {
        showErrorToast({
          title: "Error",
          message: result?.message || "Failed to process stock transfer.",
        });
      }
    },
    onError: () => {
      showErrorToast({
        title: "Error",
        message: "Internal server error.",
      });
    },
  });

  const discrepancyReviewPending = (details.stock_list || []).some(
    item => normalizeQty(item.difference_qty) !== 0 && !item.resolved_at
  );
  const canProcessTransfer = showActions && (counterAdminUser || managerUser) && details.status === "created";
  const canResolveDiscrepancy =
    managerUser && details.status === "discrepancy" && discrepancyReviewPending;
  const allReceivedZero = receivedItems.length > 0 && receivedItems.every(item => normalizeQty(item.received_qty) === 0);
  const hasAnyReceivedQty = receivedItems.some(item => normalizeQty(item.received_qty) > 0);
  const hasMissingPositiveDifferenceType = receivedItems.some(
    item => item.difference_qty > 0 && !item.difference_type
  );
  const discrepancyItemsForReview = receivedItems.filter(item => item.difference_qty !== 0);
  const hasMissingDiscrepancyDecision = discrepancyItemsForReview.some(
    item => item.difference_qty < 0 && !item.difference_type
  );
  const processedBy =
    details.processed_by_name ||
    details.stock_list?.find(item => item.received_by)?.received_by ||
    null;
  const processedAt =
    details.processed_at ||
    details.stock_list?.find(item => item.received_at)?.received_at ||
    null;
  const resolvedBy = details.stock_list?.find(item => item.resolved_by)?.resolved_by || null;
  const resolvedAt = details.stock_list?.find(item => item.resolved_at)?.resolved_at || null;

  const handleAddExcessProduct = selectedProduct => {
    const newItem = {
      id: null, // New item
      product: selectedProduct.title,
      product_id: selectedProduct.id,
      batch_no: selectedProduct.batch.label.split(" (")[0],
      batch_id: selectedProduct.batch.value,
      mrp: selectedProduct.mrp,
      selling_price: selectedProduct.selling_price,
      stock: 0,
      received_qty: 1, // Default to 1
      difference_qty: 1,
      difference_type: "EXTRA_SENT",
      units: selectedProduct.units || null,
      is_new: true,
    };

    setReceivedItems(prev => [...prev, newItem]);
  };

  const handleRemoveExcessProduct = index => {
    setReceivedItems(prev => prev.filter((_, i) => i !== index));
  };

  const buildTransferPayload = status => {
    if (status === "resolve_discrepancy") {
      return {
        stock_list_id: stockTransferId,
        status,
        stock_list: discrepancyItemsForReview.map(item => ({
          id: item.id,
          product_id: item.product_id,
          batch_id: item.batch_id,
          difference_type: item.difference_type,
        })),
      };
    }

    return {
      stock_list_id: stockTransferId,
      status,
      stock_list: receivedItems.map(item => ({
        id: item.id,
        product_id: item.product_id, // Important for new items
        batch_id: item.batch_id, // Important for batch correction
        received_qty: normalizeQty(item.received_qty),
        difference_type: item.difference_qty > 0 ? item.difference_type : null,
      })),
    };
  };

  const handleReceivedQtyChange = (index, value) => {
    setReceivedItems(currentItems =>
      currentItems.map((item, itemIndex) => {
        if (itemIndex !== index) {
          return item;
        }

        return recalculateReceivedItem({
          ...item,
          received_qty: normalizeQty(value),
        });
      })
    );
  };

  const handleDifferenceTypeChange = (index, value) => {
    setReceivedItems(currentItems =>
      currentItems.map((item, itemIndex) => {
        if (itemIndex !== index) {
          return item;
        }

        return recalculateReceivedItem({
          ...item,
          difference_type: value || null,
        });
      })
    );
  };

  const handleBatchChange = (index, batchId) => {
    setReceivedItems(currentItems =>
      currentItems.map((item, itemIndex) => {
        if (itemIndex !== index) {
          return item;
        }

        // Find the batch in the cached batches to get the batch_no
        const productBatchesList = productBatches[item.product_id] || [];
        const selectedBatch = productBatchesList.find(b => b.value === batchId);

        return {
          ...item,
          batch_id: batchId,
          batch_no: selectedBatch ? selectedBatch.label : item.batch_no,
        };
      })
    );
  };

  const fetchBatchesForProduct = async (productId) => {
    if (productBatches[productId]) return;

    try {
      const response = await stock_apis.get_product_batches(productId);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const batches = (response.data || []).map(b => {
        const stockQty = normalizeQty(b.stock);
        const stockStr = `Stock: ${stockQty.toFixed(0)}`;
        const expDate = b.exp_date ? new Date(b.exp_date) : null;

        let expInfo = "";
        let isExpired = false;

        if (expDate) {
          expDate.setHours(0, 0, 0, 0);
          if (expDate < today) {
            isExpired = true;
            expInfo = "⚠️ EXPIRED";
          } else {
            const diffTime = Math.abs(expDate - today);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            expInfo = `${diffDays} days left`;
          }
        } else {
          expInfo = "No Exp";
        }

        return {
          value: b.id,
          label: `${b.batch_no} (${stockStr})`,
          batch_no: b.batch_no,
          stockStr,
          expInfo,
          isExpired,
        };
      });
      setProductBatches(prev => ({ ...prev, [productId]: batches }));
    } catch (error) {
      console.error("Failed to fetch batches", error);
    }
  };

  const openProcessConfirmation = status => {
    const isReject = status === "reject";
    const isDiscrepancyReview = status === "resolve_discrepancy";

    openConfirmModal({
      centered: true,
      title: isReject
        ? "Reject this stock transfer?"
        : isDiscrepancyReview
          ? "Approve discrepancy review?"
          : "Accept this stock transfer?",
      children: (
        <Text size="sm">
          {isReject
            ? "This will return the full sent quantity to the source location."
            : isDiscrepancyReview
              ? "This will finalize the discrepancy review and apply pending source-side corrections for NOT_SENT and EXTRA_SENT items."
              : "This will add only the received quantity to the destination. Any shortage or extra-sent discrepancy will wait for manager review before the source-side correction is applied."}
        </Text>
      ),
      labels: {
        confirm: isReject
          ? "Reject Transfer"
          : isDiscrepancyReview
            ? "Approve Discrepancy"
            : "Accept Transfer",
        cancel: "Cancel",
      },
      confirmProps: { color: isReject ? "red" : isDiscrepancyReview ? "blue" : "green" },
      onConfirm: () => {
        processTransferMutation.mutate(buildTransferPayload(status));
      },
    });
  };

  const handleRejectTransfer = () => {
    if (!receivedItems.length) {
      showErrorToast({
        title: "Error",
        message: "No transfer items found.",
      });
      return;
    }

    if (!allReceivedZero) {
      showErrorToast({
        title: "Error",
        message: "Reject is allowed only when every received quantity is 0.",
      });
      return;
    }

    openProcessConfirmation("reject");
  };

  const handleAcceptTransfer = () => {
    if (!receivedItems.length) {
      showErrorToast({
        title: "Error",
        message: "No transfer items found.",
      });
      return;
    }

    if (!hasAnyReceivedQty) {
      showErrorToast({
        title: "Error",
        message: "Nothing was received. Use reject instead of accept.",
      });
      return;
    }

    if (hasMissingPositiveDifferenceType) {
      showErrorToast({
        title: "Error",
        message: "Select a difference type for every excess item.",
      });
      return;
    }

    openProcessConfirmation("accept");
  };

  const handleResolveDiscrepancy = () => {
    if (!discrepancyItemsForReview.length) {
      showErrorToast({
        title: "Error",
        message: "No discrepancy items are waiting for admin review.",
      });
      return;
    }

    if (hasMissingDiscrepancyDecision) {
      showErrorToast({
        title: "Error",
        message: "Choose LOSS, NOT_SENT, or WRONG_ENTRY for every shortage item before approving the discrepancy.",
      });
      return;
    }

    openProcessConfirmation("resolve_discrepancy");
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        zIndex={10}
        styles={{ header: { borderBottom: "1px solid #eee" } }}
        title={
          <Box px={10}>
            <Text fw={600}>Stock Transfer Details</Text>
          </Box>
        }
        size="960px"
        centered
      >
        <Box px={10} pos="relative">
          <LoadingOverlay visible={detailsQuery.isLoading} overlayBlur={2} />
          <Divider mb={20} opacity={0.4} />
          <Grid mb={20}>
            <Col span={12}>
              <Text size="sm" mb={15}>
                Products Details
              </Text>
              <Table withBorder withColumnBorders striped>
                <thead>
                  <tr>
                    <th>No.</th>
                    <th>Product</th>
                    <th>Batch</th>
                    <th>Sent Qty</th>
                    <th>Received Qty</th>
                    <th>Difference</th>
                    <th>Difference Type</th>
                    <th>MRP</th>
                    <th>Price</th>
                    <th>Total Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {details.stock_list?.map((item, index) => (
                    <tr key={item.id || index} style={getRowStyle(item, details.status)}>
                      <td width={50}>{index + 1}</td>
                      <td>{item.product}</td>
                      <td>{item.batch_no}</td>
                      <td>{formatQty(item.original_qty ?? item.stock, item.units)}</td>
                      <td>{details.status === "created" ? "-" : formatQty(item.received_qty, item.units)}</td>
                      <td>{details.status === "created" ? "-" : formatQty(item.difference_qty, item.units)}</td>
                      <td>{details.status === "created" ? "-" : getDifferenceTypeLabel(item)}</td>
                      <td>Rs {normalizeQty(item.mrp).toFixed(2)}</td>
                      <td>Rs {normalizeQty(item.selling_price).toFixed(2)}</td>
                      <td>Rs {(normalizeQty(item.stock) * normalizeQty(item.selling_price)).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Col>
            <Col span={12}>
              <Grid align="end">
                <Col span={3}>
                  <Text size="sm" mb={15}>
                    Payment Details
                  </Text>
                  <Table withBorder>
                    <tbody>
                      <tr>
                        <td>
                          <Text opacity={0.5}>Total Amount</Text>
                        </td>
                        <td>
                          <Text fw={600}>Rs {normalizeQty(details.amount).toFixed(2)}</Text>
                        </td>
                      </tr>
                      {details.original_stock_id && (
                        <tr>
                          <td>
                            <Text opacity={0.5}>Original Transfer</Text>
                          </td>
                          <td>
                            <Text fw={600}>#{details.original_stock_id}</Text>
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td>
                          <Text opacity={0.5}>Status</Text>
                        </td>
                        <td>
                          <Text fw={600}>{details.status || "-"}</Text>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Col>
                <Col span={9}>
                  <Group position="right">
                    <Button variant="light" onClick={() => setReceivedModalOpened(true)}>
                      {canProcessTransfer
                        ? "Process"
                        : canResolveDiscrepancy
                          ? "Review Discrepancy"
                          : "Received Details"}
                    </Button>
                  </Group>
                  {details.status !== "created" && (
                    <Text mt={10} size="sm" c="dimmed" ta="right">
                      Processed by {processedBy || "-"} on {formatDateTime(processedAt)}
                    </Text>
                  )}
                  {resolvedAt && (
                    <Text mt={4} size="sm" c="dimmed" ta="right">
                      Discrepancy reviewed by {resolvedBy || "-"} on {formatDateTime(resolvedAt)}
                    </Text>
                  )}
                </Col>
              </Grid>
            </Col>
          </Grid>

          {canProcessTransfer && (
            <>
              <Divider mb={20} opacity={0.4} />
              <Text size="sm" c="dimmed" ta="right">
                Reject is available only when every received quantity is 0. Any positive receipt must be completed through accept.
              </Text>
            </>
          )}
          {canResolveDiscrepancy && (
            <>
              <Divider mb={20} opacity={0.4} />
              <Text size="sm" c="dimmed" ta="right">
                This transfer has discrepancies waiting for manager review. Shortages need LOSS, NOT_SENT, or WRONG_ENTRY, and EXTRA_SENT items need manager approval before source stock is adjusted.
              </Text>
            </>
          )}
        </Box>
      </Modal>

      <Modal
        opened={receivedModalOpened}
        onClose={() => setReceivedModalOpened(false)}
        zIndex={20}
        styles={{ header: { borderBottom: "1px solid #eee" } }}
        title={
          <Box px={10}>
            <Text fw={600}>
              {details.status === "loss_report"
                ? "Stock Loss Audit Report"
                : canProcessTransfer
                  ? "Stock Transfer Receiving"
                  : canResolveDiscrepancy
                    ? "Discrepancy Review"
                    : "Stock Transfer Details"}
            </Text>
          </Box>
        }
        size="960px"
        centered
      >
        <Box px={10} pos="relative">
          <LoadingOverlay visible={processTransferMutation.isLoading} overlayBlur={2} />
          <Divider mb={20} opacity={0.4} />
          <Text size="sm" mb={15} c="dimmed">
            {details.status === "loss_report"
              ? "This is a descriptive record of stock lost during a previous transfer. It documents that the stock is no longer available at the warehouse or counter."
              : canProcessTransfer
                ? "Enter the physical received quantity for each item. If there is a shortage, the receiver only records it and the manager decides later whether it is LOSS, NOT_SENT, or WRONG_ENTRY. If the excess came from a wrong entry, correct the received quantity before accepting. Use EXTRA_SENT only for real excess stock, and it will wait for manager approval."
                : canResolveDiscrepancy
                  ? "Review the recorded discrepancies. Choose LOSS, NOT_SENT, or WRONG_ENTRY for shortages, then approve the discrepancy so pending source-side corrections are applied."
                  : "Received quantities and discrepancy decisions are shown below."}
          </Text>
          <Table withBorder withColumnBorders striped>
            <thead>
              <tr>
                <th>No.</th>
                <th>Product</th>
                <th>Batch</th>
                <th>Sent Qty</th>
                <th>Received Qty</th>
                <th>Difference</th>
                <th>Difference Type</th>
              </tr>
            </thead>
            <tbody>
              {receivedItems.map((item, index) => {
                const differenceOptions = POSITIVE_DIFFERENCE_OPTIONS;

                return (
                  <tr key={item.id || `new-${index}`} style={getRowStyle(item, details.status)}>
                    <td width={50}>
                      {index + 1}
                      {canProcessTransfer && item.id === null && (
                        <ActionIcon color="red" variant="subtle" onClick={() => handleRemoveExcessProduct(index)}>
                          <IconTrash size={16} />
                        </ActionIcon>
                      )}
                    </td>
                    <td>{item.product}</td>
                    <td>
                      {canResolveDiscrepancy && item.difference_qty > 0 ? (
                        <Select
                          value={item.batch_id}
                          onDropdownOpen={() => fetchBatchesForProduct(item.product_id)}
                          onChange={value => handleBatchChange(index, value)}
                          data={
                            productBatches[item.product_id] || [
                              { value: item.batch_id, label: item.batch_no },
                            ]
                          }
                          searchable
                          nothingFound="No batches found"
                          itemComponent={BatchSelectItem}
                          withinPortal
                          styles={{ dropdown: { minWidth: 280 } }}
                        />
                      ) : (
                        item.batch_no
                      )}
                    </td>
                    <td>{formatQty(item.stock, item.units)}</td>
                    <td width={160}>
                      {canProcessTransfer ? (
                        <NumberInput
                          value={item.received_qty}
                          onChange={value => handleReceivedQtyChange(index, value)}
                          min={0}
                          max={item.stock > 0 ? item.stock : undefined}
                          precision={["PCS", "NOS"].includes(item.units?.toUpperCase()) ? 0 : 3}
                          step={["PCS", "NOS"].includes(item.units?.toUpperCase()) ? 1 : 0.001}
                        />
                      ) : (
                        formatQty(item.received_qty, item.units)
                      )}
                    </td>
                    <td>{formatQty(item.difference_qty, item.units)}</td>
                    <td width={180}>
                      {item.difference_qty === 0 ? (
                        <Text size="sm" c="dimmed">
                          MATCHED
                        </Text>
                      ) : canProcessTransfer && item.difference_qty > 0 ? (
                        <Select
                          value={item.difference_type}
                          onChange={value => handleDifferenceTypeChange(index, value)}
                          data={differenceOptions}
                          placeholder="Select type"
                          withinPortal
                        />
                      ) : canProcessTransfer && item.difference_qty < 0 ? (
                        <Text size="sm" c="dimmed">
                          Admin will decide
                        </Text>
                      ) : canResolveDiscrepancy && item.difference_qty < 0 ? (
                        <Select
                          value={item.difference_type}
                          onChange={value => handleDifferenceTypeChange(index, value)}
                          data={NEGATIVE_DIFFERENCE_OPTIONS}
                          placeholder="Select type"
                          withinPortal
                        />
                      ) : canResolveDiscrepancy && item.difference_qty > 0 ? (
                        <Text size="sm" c="dimmed">
                          EXTRA_SENT
                        </Text>
                      ) : (
                        getDifferenceTypeLabel(item)
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          {canProcessTransfer ? (
            <Group position="apart" mt={20}>
              <Button
                variant="light"
                leftIcon={<IconPlus size={16} />}
                onClick={() => setSelectProductModalOpened(true)}
              >
                Add Excess Product
              </Button>
              <Group>
                <Button variant="outline" color="red" onClick={handleRejectTransfer} disabled={!allReceivedZero}>
                  Reject Transfer
                </Button>
                <Button
                  color="green"
                  onClick={handleAcceptTransfer}
                  disabled={!hasAnyReceivedQty || hasMissingPositiveDifferenceType}
                >
                  Accept Transfer
                </Button>
              </Group>
            </Group>
          ) : canResolveDiscrepancy ? (
            <Group position="right" mt={20}>
              <Button variant="light" onClick={() => setReceivedModalOpened(false)}>
                Close
              </Button>
              <Button color="blue" onClick={handleResolveDiscrepancy} disabled={hasMissingDiscrepancyDecision}>
                Approve Discrepancy
              </Button>
            </Group>
          ) : (
            <Group position="right" mt={20}>
              <Button variant="light" onClick={() => setReceivedModalOpened(false)}>
                Close
              </Button>
            </Group>
          )}
        </Box>
      </Modal>

      <SelectProductModal
        opened={selectProductModalOpened}
        onClose={() => setSelectProductModalOpened(false)}
        handleAddProduct={handleAddExcessProduct}
        returnMaster={true}
      />
    </>
  );

}

StockTransferDetailsModal.propTypes = {
  opened: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  stockTransferId: PropTypes.number,
  showActions: PropTypes.bool,
};

export default StockTransferDetailsModal;

